import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { TOUR_SPOTLIGHT_PADDING_PX, type TourStep } from '../constants';
import type { FirstAccessTour } from '../hooks/useFirstAccessTour';
import { computeSpotlightRect, findTourTarget, type SpotlightRect } from '../utils/spotlight';

const CARD_GAP_PX = 16;
const BACKDROP_SHADOW = '0 0 0 9999px rgb(15 23 42 / 0.7)';

function useSpotlightRect(step: TourStep): SpotlightRect | null {
  const [rect, setRect] = useState<SpotlightRect | null>(null);

  useEffect(() => {
    const measure = () => {
      const element = step.target ? findTourTarget(document, step.target) : null;
      setRect(element ? computeSpotlightRect(element.getBoundingClientRect(), TOUR_SPOTLIGHT_PADDING_PX) : null);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [step]);

  return rect;
}

/**
 * Full-screen overlay that highlights the current tour step target and shows its explanation card.
 * @param props - Tour state and actions from `useFirstAccessTour`.
 */
export function TourOverlay({ step, stepIndex, totalSteps, isFirst, isLast, next, back, finish }: FirstAccessTour) {
  const rect = useSpotlightRect(step);
  const titleId = `tour-title-${step.id}`;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      {rect ? (
        <div
          data-testid="tour-spotlight"
          className="pointer-events-none absolute rounded-2xl transition-all duration-300"
          style={{ ...rect, boxShadow: BACKDROP_SHADOW }}
        />
      ) : (
        <div className="absolute inset-0 bg-slate-900/70" />
      )}

      <div
        className={
          rect
            ? 'absolute inset-x-4 mx-auto max-w-sm'
            : 'absolute inset-x-4 top-1/2 mx-auto max-w-sm -translate-y-1/2'
        }
        style={rect ? { bottom: window.innerHeight - rect.top + CARD_GAP_PX } : undefined}
      >
        <div className="rounded-2xl bg-white p-5 shadow-xl dark:bg-slate-900">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
            {stepIndex + 1} de {totalSteps}
          </p>
          <h2 id={titleId} className="mt-1 text-base font-semibold text-slate-900 dark:text-slate-100">
            {step.title}
          </h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{step.description}</p>

          <div className="mt-5 flex items-center justify-between gap-2">
            {isLast ? (
              <span />
            ) : (
              <Button variant="ghost" size="sm" onClick={finish}>
                Pular
              </Button>
            )}
            <div className="flex gap-2">
              {!isFirst && (
                <Button variant="outline" size="sm" onClick={back}>
                  Voltar
                </Button>
              )}
              <Button size="sm" onClick={next}>
                {isLast ? 'Concluir' : 'Próximo'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
